// src/modules/app-views/crm/pages/Document/tabs/FollowUpTab/FollowUpPanel.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Card, Flex, Input, Tooltip, Typography, theme } from 'antd';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import { CheckOutlined, ClockCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import { IoHandLeftOutline } from 'react-icons/io5';
import { BiCollapseAlt, BiExpandAlt } from 'react-icons/bi';
import { useDocumentContext } from 'components/core/DocumentProvider';
import CrmCardHeaderBottom from '../../../../components/CardHeaderBottom';
import useZustand from 'hooks/useZustand';
import crmService from '../../../../service';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Fixed width for the meta column (Avatar + name + date) inside each timeline item
const META_WIDTH = 130;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FollowUpPanel = ({ isFullscreen, onToggleFullscreen }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, refresh } = useDocumentContext();
  const storedUser = useZustand('auth', 'user');
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const textareaRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isFiled = !!data?._metadata?.audit?.filedAt;
  const isRescheduled = !!data?.attendance?.rescheduledAt && !isFiled;
  const isDealClosed = data?.status === 'contract_closed' || data?.status === 'lost';
  const isAttending = !!data?.attendance?.startedAt && !isFiled && !isRescheduled && !isDealClosed;

  // SLA configuration from board/column parameters
  const boards = storedParameters?.modules?.crm?.boards || [];
  const board = boards.find(b => b.uid === data?.boardUid) || null;
  const column = board?.columns?.find(c => c.uid === data?.columnUid) || null;
  const columnSla = column?.automation?.sla;
  const boardBusinessHours = board?.businessHours || null;
  const boardHolidays = board?.holidays || [];
  const slaLimitBusinessMinutes =
    columnSla?.businessTime?.minutes || columnSla?.businessMinutes || 0;
  const slaLimitHours = columnSla?.elapsedTime
    ? (columnSla.elapsedTime.minutes || 0) / 60
    : columnSla?.elapsedHours || 0;
  const hasSla = slaLimitBusinessMinutes > 0 || slaLimitHours > 0;

  // Sort newest first
  const followUpItems = [...(data?.followUp || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Live SLA countdown
  const [elapsedMs, setElapsedMs] = useState(() => crmService.computeElapsedMs(data));
  const [bizSecElapsed, setBizSecElapsed] = useState(() => {
    if (!slaLimitBusinessMinutes) return 0;
    const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
    return crmService.computeBusinessSecondsElapsed(
      crmService.getCardInAt(data),
      schedule,
      boardHolidays
    );
  });
  const [isInBizHours, setIsInBizHours] = useState(() => {
    if (!slaLimitBusinessMinutes) return true;
    const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
    return crmService.checkIsInBusinessHours(schedule, boardHolidays);
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Focus textarea on mount (tab open) and whenever fullscreen toggles
  useEffect(() => {
    if (!isAttending) return;
    // Small delay ensures the DOM is ready after fullscreen transition
    const id = setTimeout(() => textareaRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [isFullscreen, isAttending]);

  // Tick SLA every second while attending — stopped when rescheduled, finalized, or not yet started
  const isFinalized = data?.status === 'contract_closed' || data?.status === 'lost';
  useEffect(() => {
    if (!hasSla || isRescheduled || isFinalized) return;
    const tick = () => {
      setElapsedMs(crmService.computeElapsedMs(data));
      if (slaLimitBusinessMinutes > 0) {
        const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
        const inAt = crmService.getCardInAt(data);
        setBizSecElapsed(crmService.computeBusinessSecondsElapsed(inAt, schedule, boardHolidays));
        setIsInBizHours(crmService.checkIsInBusinessHours(schedule, boardHolidays));
      }
    };
    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [data?.columnUid, slaLimitBusinessMinutes, slaLimitHours, boardBusinessHours, isRescheduled]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(async () => {
    const text = comment.trim();
    if (!text || !data?._id) return;

    setSubmitting(true);
    try {
      await crmService.addFollowUp(data._id, {
        accountId: storedUser?._id,
        accountName: storedUser?.name || null,
        accountAvatarUrl: storedUser?.avatar?.base64Thumb || storedUser?.avatar?.thumbUrl || null,
        text,
      });
      setComment('');
      refresh?.({ showLoading: false });
    } catch {
      appService.message({ flag: 'e', text: 'error_saving' });
    } finally {
      setSubmitting(false);
    }
  }, [comment, data?._id, storedUser, refresh]);

  const handleKeyDown = useCallback(
    e => {
      // Ctrl+Enter or Cmd+Enter submits
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    },
    [handleAdd]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const sla = computeSlaDisplay({
    slaLimitBusinessMinutes,
    slaLimitHours,
    bizSecElapsed,
    elapsedMs,
    isInBizHours,
    hasSla,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical gap={16} style={{ padding: 16 }}>
      {/* Info + Steps cards — only in fullscreen, 100% column width */}
      {isFullscreen && (
        <>
          <Card size="small" className="with-shadow" style={{ margin: 24 }}>
            <CrmCardHeaderBottom
              boardUid={data?.boardUid}
              columnUid={data?.columnUid}
              status={data?.status}
              isFullscreen={isFullscreen}
            />
          </Card>
        </>
      )}

      {/* Input wrapper — max 640px, centered */}
      <div style={{ maxWidth: 640, width: '100%', padding: '0 24px', margin: '0 auto' }}>
        {/* Textarea — always visible, disabled when not attending */}
        <Input.TextArea
          ref={textareaRef}
          value={comment}
          onChange={e => setComment(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={translateX('type_followup_hint')}
          autoSize={{ minRows: 2, maxRows: 5 }}
          disabled={!isAttending || submitting}
          style={{ marginBottom: 8 }}
        />

        {/* Actions row: SLA / rescheduled indicator (left) | maximize + add (right) */}
        <Flex justify="space-between" align="start">
          {/* Left: SLA countdown when attending, paused indicator when rescheduled */}
          {isRescheduled ? (
            <Tooltip
              title={`${translateX('rescheduled_to')}: ${data?.attendance?.rescheduledTo ? dayjs(data.attendance.rescheduledTo).format('DD/MM/YY HH:mm') : '—'}`}
            >
              <Flex gap={6} align="center" style={{ cursor: 'default' }}>
                <span style={{ color: '#1677ff', display: 'inline-flex', alignItems: 'center' }}>
                  <PauseCircleOutlined style={{ fontSize: 16 }} />
                </span>
                <Typography.Text strong style={{ color: '#1677ff', fontSize: 13 }}>
                  {translateX('sla_paused')}
                </Typography.Text>
              </Flex>
            </Tooltip>
          ) : isAttending && sla.visible ? (
            <Tooltip title={sla.tooltip}>
              <Flex gap={6} align="center">
                <span style={{ color: sla.color, display: 'inline-flex', alignItems: 'center' }}>
                  {sla.icon}
                </span>
                <Typography.Text strong style={{ color: sla.color, fontSize: 13 }}>
                  {sla.label}
                </Typography.Text>
              </Flex>
            </Tooltip>
          ) : (
            <span />
          )}

          {/* Right side: maximize button + add button */}
          <Flex gap={8} align="center">
            <Tooltip
              title={isFullscreen ? translateX('restore') : translateX('maximize')}
              placement="left"
            >
              <Button
                type="default"
                size="small"
                disabled={!isAttending}
                style={{
                  right: 0,
                  top: 0,
                  position: 'absolute',
                  marginTop: isFullscreen ? 0 : -17,
                  marginRight: 0,
                }}
                icon={
                  isFullscreen ? (
                    <BiCollapseAlt style={{ fontSize: 18, display: 'flex' }} />
                  ) : (
                    <BiExpandAlt style={{ fontSize: 18, display: 'flex' }} />
                  )
                }
                onClick={onToggleFullscreen}
              />
            </Tooltip>

            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleAdd}
              loading={submitting}
              disabled={!isAttending || !comment.trim()}
            >
              {translateX('add')}
            </Button>
          </Flex>
        </Flex>
      </div>

      {/* Follow-up list — manual layout: [meta] [dot+line] [text] */}
      {followUpItems.length > 0 ? (
        <Flex vertical>
          {followUpItems.map((item, idx) => {
            const acct = item.__account;
            const name =
              acct?.fullName ||
              acct?.name ||
              item.accountName ||
              (item.accountId === storedUser?._id ? storedUser?.name : null) ||
              '—';
            const avatarUrl =
              acct?.avatar?.base64Thumb ||
              acct?.avatar?.thumbUrl ||
              acct?.pictureUrl ||
              item.accountAvatarUrl ||
              (item.accountId === storedUser?._id
                ? storedUser?.avatar?.base64Thumb || storedUser?.avatar?.thumbUrl
                : null) ||
              undefined;
            const backColor =
              acct?.backColor ||
              (item.accountId === storedUser?._id ? storedUser?.backColor : null) ||
              undefined;
            const foreColor =
              acct?.foreColor ||
              (item.accountId === storedUser?._id ? storedUser?.foreColor : null) ||
              undefined;
            const isAutoEntry = item.entryType && item.entryType !== 'comment';
            const isLast = idx === followUpItems.length - 1;

            return (
              <Flex key={item._id || idx} gap={0} align="stretch">
                {/* Meta column: avatar + name + date */}
                <Flex
                  vertical
                  gap={2}
                  align="flex-end"
                  style={{
                    width: META_WIDTH,
                    flexShrink: 0,
                    paddingRight: 10,
                    paddingTop: 12,
                    paddingBottom: 12,
                  }}
                >
                  <Flex gap={6} align="center" justify="flex-end">
                    <SimpleAvatar
                      size={22}
                      src={avatarUrl}
                      name={name}
                      backColor={backColor}
                      foreColor={foreColor}
                      showBorder={false}
                      showShadow={false}
                      hideLoading
                    />
                    <Typography.Text strong style={{ fontSize: 11, lineHeight: 1.2 }}>
                      {name}
                    </Typography.Text>
                  </Flex>
                  <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                    {item.createdAt ? dayjs(item.createdAt).format('DD/MM/YY HH:mm:ss') : '—'}
                  </Typography.Text>
                </Flex>

                {/* Dot + vertical line — uniform style for all entries */}
                <Flex
                  vertical
                  align="center"
                  style={{ flexShrink: 0, paddingLeft: 4, paddingTop: 19 }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: isAutoEntry ? token.colorTextQuaternary : token.colorPrimary,
                      flexShrink: 0,
                    }}
                  />
                  {!isLast && (
                    <div
                      style={{
                        flex: 1,
                        width: 2,
                        background: token.colorBorderSecondary,
                        marginTop: 4,
                        minHeight: 20,
                      }}
                    />
                  )}
                </Flex>

                {/* Content column: uniform text style */}
                <Flex style={{ flex: 1, paddingLeft: 10, paddingTop: 12, paddingBottom: 12 }}>
                  <Typography.Text
                    type={isAutoEntry ? 'secondary' : undefined}
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      ...(isAutoEntry ? { fontStyle: 'italic', fontSize: 12 } : {}),
                    }}
                  >
                    {item.text}
                  </Typography.Text>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      ) : (
        <Flex justify="center" style={{ padding: '48px 0', color: '#bbb' }}>
          <Typography.Text type="secondary">{translateX('no_followup_yet')}</Typography.Text>
        </Flex>
      )}
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FollowUpPanel;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function computeSlaDisplay({
  slaLimitBusinessMinutes,
  slaLimitHours,
  bizSecElapsed,
  elapsedMs,
  isInBizHours,
  hasSla,
}) {
  if (!hasSla) return { visible: false };

  let label = null;
  let color = '#8c8c8c';
  let tooltip = '';
  let icon = <ClockCircleOutlined style={{ fontSize: 16 }} />;

  if (slaLimitBusinessMinutes > 0) {
    const limitSec = slaLimitBusinessMinutes * 60;
    const remainingSec = limitSec - bizSecElapsed;
    const pct = remainingSec / limitSec;
    color = crmService.getSlaColor(pct);
    label = crmService.formatDurationMs(remainingSec * 1_000);
    tooltip = isInBizHours ? translateX('time_running') : translateX('time_paused');
    icon = isInBizHours ? (
      <ClockCircleOutlined style={{ fontSize: 16 }} />
    ) : (
      <IoHandLeftOutline style={{ fontSize: 16 }} />
    );
  } else if (slaLimitHours > 0) {
    const limitMs = slaLimitHours * 3_600_000;
    const remainingMs = limitMs - elapsedMs;
    const pct = remainingMs / limitMs;
    color = crmService.getSlaColor(pct);
    label = crmService.formatDurationMs(remainingMs);
    tooltip = translateX('elapsed_time');
  }

  return { visible: !!label, label, color, tooltip, icon };
}
